import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { YesNo } from "@/base/wizard/YesNo";
import { buttonClass, TOUCH_TARGET } from "@/components/buttonStyles";
import { FIELD_ERROR, FIELD_HELP, FIELD_LABEL } from "@/components/fieldStyles";
import { NumericField } from "@/components/NumericField";
import { SelectField } from "@/components/SelectField";
import { TextField } from "@/components/TextField";
import { localTodayIso } from "@/lib/dates";
import { sentenceCaseKey } from "@/lib/listFormat";
import type { BrokerQuote } from "@/lib/submissions/brokerSubmissions";
import {
  billingOptions,
  bindBody,
  bindFormErrors,
  bindFormFrom,
  planOptions,
  type BindContactForm,
  type BindForm,
} from "@/lib/submissions/bindForm";
import {
  BIND_BLOCKER_LABELS,
  BIND_NOTE_MAX,
  bindFailedLine,
  bindRefusalOf,
  bindStanding,
  KEY_REUSED_MESSAGE,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  requestBind,
  type BindBlocker,
  type BindResult,
} from "@/lib/submissions/quoteBinding";

// BRK-S209 — asking Signal to bind a quote (PMS-S555), in its popup: the
// billing elections, the carrier's bind details, and the broker's word that
// the insured asked. No carrier binds by API yet, so this is a request in
// Signal's queue, and what shows after is PMS's answer only (NN #7): a
// request never reads as bound. The payment method is its type: the portal
// never asks for a card or bank number.
//
// One Idempotency-Key per attempt (NN #9: in this view's state, never
// stored): the same key goes again on a retry after no answer, which PMS
// answers with the request it already made; a new one only after an answer.
//
// A carrier's failed bind call (BRK-S504, PMS-S907) is an answer, never a
// decline: its 502, or a replay answering `failed`, reads in the portal's
// words, and Try Again makes a new request.

const TEXTAREA =
  "w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30";

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1 text-subheading text-brk-text">{title}</legend>
      {children}
    </fieldset>
  );
}

/** Why PMS won't take it, each reason on its own line. */
function Blockers({ blockers }: { blockers: BindBlocker[] }) {
  return (
    <div role="alert" className="flex flex-col gap-1 text-sm">
      <p className="font-medium text-brk-danger">
        Signal can’t take a bind request for this quote yet:
      </p>
      <ul className="list-inside list-disc text-brk-text">
        {blockers.map((b) => (
          <li key={b.code}>
            {BIND_BLOCKER_LABELS[b.code]}
            {b.code === "missing_fields" &&
              b.fields.length > 0 &&
              ` (${b.fields.map(sentenceCaseKey).join(", ")})`}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContactFields({
  title,
  value,
  errors,
  prefix,
  onChange,
  disabled,
}: {
  title: string;
  value: BindContactForm;
  errors: Record<string, string>;
  prefix: "primaryContact" | "accountingContact";
  onChange: (next: BindContactForm) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="font-medium text-brk-text">{title}</p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <TextField
          label="Name"
          optional
          autoComplete="name"
          maxLength={200}
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
          error={errors[`${prefix}.name`]}
          disabled={disabled}
        />
        <TextField
          label="Email"
          optional
          type="email"
          autoComplete="email"
          maxLength={320}
          value={value.email}
          onChange={(e) => onChange({ ...value, email: e.target.value })}
          error={errors[`${prefix}.email`]}
          disabled={disabled}
        />
        <TextField
          label="Phone"
          optional
          type="tel"
          autoComplete="tel"
          maxLength={40}
          value={value.phone}
          onChange={(e) => onChange({ ...value, phone: e.target.value })}
          error={errors[`${prefix}.phone`]}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

type Problem =
  | { kind: "network"; message: string }
  | { kind: "blockers"; blockers: BindBlocker[] }
  /** The carrier's call failed: sent again, it's a new request. */
  | { kind: "failed"; message: string }
  | { kind: "message"; message: string };

export function BindQuoteStep({
  submissionId,
  quote,
  carrier,
  onBack,
  onChanged,
}: {
  submissionId: string;
  quote: BrokerQuote;
  carrier: string;
  /** Back to the offer's details. */
  onBack: () => void;
  /** Reads the quotes again, once PMS has answered. */
  onChanged: () => void | Promise<void>;
}) {
  const { authedRequest } = useAuth();
  const today = localTodayIso();
  const [form, setForm] = useState<BindForm>(() => bindFormFrom(quote, today));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<Problem | null>(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<BindResult | null>(null);
  // This attempt's key, until PMS answers it.
  const [attemptKey, setAttemptKey] = useState<string | null>(null);
  const set = (patch: Partial<BindForm>) =>
    setForm((f) => ({ ...f, ...patch }));

  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);

  // PMS holds the failed request: the quotes are read again, and the form
  // stays for Try Again.
  function carrierFailed() {
    setProblem({ kind: "failed", message: bindFailedLine(carrier) });
    void onChanged();
  }

  async function send(key: string, reusedOnce = false): Promise<void> {
    setSending(true);
    setProblem(null);
    setAttemptKey(key);
    try {
      const answer = await requestBind(authedRequest, submissionId, quote.id, {
        idempotencyKey: key,
        body: bindBody(form),
      });
      setAttemptKey(null);
      // A replay of a request whose carrier call failed: the 502 again.
      if (answer.outcome === "failed") {
        carrierFailed();
        return;
      }
      setResult(answer);
      void onChanged();
    } catch (err) {
      const refusal = bindRefusalOf(err);
      // No answer: the same attempt, so the same key, on a retry.
      if (refusal.kind !== "network") setAttemptKey(null);
      switch (refusal.kind) {
        case "network":
          setProblem({ kind: "network", message: refusal.message });
          break;
        case "key_reused":
          // The key named another quote's request: this one is new.
          if (!reusedOnce) return send(crypto.randomUUID(), true);
          setProblem({
            kind: "message",
            message: KEY_REUSED_MESSAGE,
          });
          break;
        case "not_bindable":
          setProblem({ kind: "blockers", blockers: refusal.blockers });
          break;
        case "carrier_failed":
          carrierFailed();
          break;
        case "fields":
          setErrors(refusal.byField);
          setProblem({ kind: "message", message: refusal.summary });
          break;
        case "conflict":
          setProblem({ kind: "message", message: refusal.message });
          void onChanged();
          break;
        case "refused":
          setProblem({ kind: "message", message: refusal.message });
          break;
      }
    } finally {
      setSending(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    // "Sending…" stays focusable (aria-busy, not disabled): a second press
    // while it sends is no second request.
    if (sending) return;
    const found = bindFormErrors(form, today);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setProblem({
        kind: "message",
        message: "Some details need attention before Signal can take it.",
      });
      return;
    }
    void send(attemptKey ?? crypto.randomUUID());
  }

  const back = (
    <button
      type="button"
      onClick={onBack}
      className={`inline-flex items-center gap-1 self-start text-sm text-brk-accent hover:underline ${TOUCH_TARGET}`}
    >
      <ArrowLeft aria-hidden="true" className="h-4 w-4" />
      Details
    </button>
  );

  // PMS's answer, or, once the quotes are read again (Check status), the
  // request it already holds.
  const answered =
    result?.quote ??
    (quote.stage === "binding" || quote.stage === "bound" ? quote : null);
  if (answered) {
    const standing = bindStanding(answered);
    return (
      <div className="flex flex-col gap-4 text-sm">
        {back}
        <div role="status" className="flex flex-col gap-1">
          <h3
            ref={heading}
            tabIndex={-1}
            className="text-subheading text-brk-text focus:outline-none"
          >
            {standing?.chip ?? "Bind requested"}
          </h3>
          <p className="text-brk-text">
            {standing?.line ??
              "Signal binds this with the carrier and confirms it here. This coverage isn’t bound yet."}
          </p>
        </div>
      </div>
    );
  }

  const plans = planOptions(quote);
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-6 text-sm">
      <div className="flex flex-col gap-1">
        {back}
        <h3
          ref={heading}
          tabIndex={-1}
          className="text-subheading text-brk-text focus:outline-none"
        >
          Request bind
        </h3>
        <p className="text-brk-dim">
          Signal binds {carrier}’s quote with the carrier and confirms it here.
          Coverage isn’t bound until then.
        </p>
      </div>

      <Group title="Billing">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <SelectField
            label="Billed by"
            required
            placeholder="Choose…"
            options={billingOptions(quote)}
            value={form.billingType}
            onChange={(e) =>
              set({ billingType: e.target.value as BindForm["billingType"] })
            }
            error={errors.billingType}
            disabled={sending}
          />
          {plans.length > 0 ? (
            <SelectField
              label="Payment plan"
              required
              placeholder="Choose…"
              options={plans}
              value={form.paymentPlan}
              onChange={(e) =>
                set({ paymentPlan: e.target.value as BindForm["paymentPlan"] })
              }
              error={errors.paymentPlan}
              disabled={sending}
            />
          ) : (
            // The carrier takes none of the quote's plans (PMS-S933,
            // BRK-S510): only Signal can change that, so there's nothing to
            // choose here.
            <p role="note" className="text-brk-danger">
              {carrier} takes none of this quote’s payment plans. Message Signal
              to sort it out.
            </p>
          )}
          <SelectField
            label="Payment method"
            optional
            placeholder="Choose…"
            options={PAYMENT_METHODS.map((value) => ({
              value,
              label: PAYMENT_METHOD_LABELS[value],
            }))}
            value={form.paymentMethod}
            onChange={(e) =>
              set({
                paymentMethod: e.target.value as BindForm["paymentMethod"],
              })
            }
            error={errors.paymentMethod}
            disabled={sending}
          />
        </div>
        <p className={FIELD_HELP}>
          The method’s type only. Signal never asks for a card or bank number
          here.
        </p>
        <YesNo
          label="Is the premium financed?"
          value={form.premiumFinanced}
          onChange={(premiumFinanced) => set({ premiumFinanced })}
          error={errors.premiumFinanced}
          disabled={sending}
        />
        {form.premiumFinanced && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <TextField
              label="Finance company"
              required
              value={form.financeCompany}
              onChange={(e) => set({ financeCompany: e.target.value })}
              error={errors["premiumFinance.company"]}
              disabled={sending}
            />
            <TextField
              label="Agreement number"
              required
              value={form.agreementRef}
              onChange={(e) => set({ agreementRef: e.target.value })}
              error={errors["premiumFinance.agreementRef"]}
              disabled={sending}
            />
            <NumericField
              kind="currency"
              label="Down payment"
              optional
              min={0}
              value={form.downPayment}
              onChange={(downPayment) => set({ downPayment })}
              error={errors["premiumFinance.downPayment"]}
            />
            <NumericField
              kind="number"
              label="Number of installments"
              optional
              min={1}
              value={form.installments}
              onChange={(installments) => set({ installments })}
              error={errors["premiumFinance.installments"]}
            />
          </div>
        )}
      </Group>

      <Group title="Bind details">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextField
            label="Effective date"
            type="date"
            required
            min={today}
            value={form.requestedEffectiveDate}
            onChange={(e) => set({ requestedEffectiveDate: e.target.value })}
            error={errors.requestedEffectiveDate}
            disabled={sending}
          />
          <TextField
            label="Application signed on"
            type="date"
            optional
            max={today}
            value={form.applicationSignedOn}
            onChange={(e) => set({ applicationSignedOn: e.target.value })}
            error={errors.applicationSignedOn}
            disabled={sending}
          />
        </div>
        <YesNo
          label="Did the insured accept terrorism coverage (TRIA)?"
          value={
            form.triaElection === "" ? null : form.triaElection === "accepted"
          }
          onChange={(accepted) =>
            set({ triaElection: accepted ? "accepted" : "rejected" })
          }
          error={errors.triaElection}
          disabled={sending}
        />
        <ContactFields
          title="Primary contact"
          prefix="primaryContact"
          value={form.primaryContact}
          errors={errors}
          onChange={(primaryContact) => set({ primaryContact })}
          disabled={sending}
        />
        <ContactFields
          title="Accounting contact"
          prefix="accountingContact"
          value={form.accountingContact}
          errors={errors}
          onChange={(accountingContact) => set({ accountingContact })}
          disabled={sending}
        />
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`${quote.id}-bind-note`} className={FIELD_LABEL}>
            Note for Signal <span className="font-normal">(optional)</span>
          </label>
          <textarea
            id={`${quote.id}-bind-note`}
            rows={3}
            maxLength={BIND_NOTE_MAX}
            value={form.note}
            onChange={(e) => set({ note: e.target.value })}
            aria-invalid={errors.note ? true : undefined}
            className={TEXTAREA}
            disabled={sending}
          />
          {errors.note && <p className={FIELD_ERROR}>{errors.note}</p>}
        </div>
      </Group>

      <div className="flex flex-col gap-1">
        <label
          className={`flex items-start gap-2 text-brk-text ${TOUCH_TARGET}`}
        >
          <input
            type="checkbox"
            checked={form.attested}
            onChange={(e) => set({ attested: e.target.checked })}
            aria-invalid={errors.attested ? true : undefined}
            disabled={sending}
            className="mt-1"
          />
          The insured has accepted these terms and asked me to bind this
          coverage.
        </label>
        {errors.attested && <p className={FIELD_ERROR}>{errors.attested}</p>}
      </div>

      {problem?.kind === "blockers" && <Blockers blockers={problem.blockers} />}
      {(problem?.kind === "message" || problem?.kind === "failed") && (
        <p role="alert" className="text-brk-danger">
          {problem.message}
        </p>
      )}
      {problem?.kind === "network" && (
        <div role="alert" className="flex flex-col gap-2">
          <p className="text-brk-danger">{problem.message}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void onChanged()}
              className={buttonClass({ variant: "secondary", size: "sm" })}
            >
              Check status
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <button
          type="submit"
          aria-busy={sending || undefined}
          className={buttonClass()}
        >
          {sending
            ? "Sending…"
            : problem?.kind === "network" || problem?.kind === "failed"
              ? "Try Again"
              : "Request bind"}
        </button>
      </div>
    </form>
  );
}
